import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/safeClient';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Background deal location tracker.
 * When the user is logged in and has active (pending/active) deals,
 * this hook periodically sends the user's GPS location to the server
 * so both buyer and seller positions are tracked — even without
 * opening the Meeting Details page.
 */
export function useBackgroundDealTracker() {
  const { user } = useAuth();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const activeDealIdsRef = useRef<string[]>([]);
  const watchIdRef = useRef<number | null>(null);
  const lastLocationRef = useRef<{ lat: number; lng: number } | null>(null);

  const sendLocationUpdate = useCallback(async (position: GeolocationPosition) => {
    if (!user || activeDealIdsRef.current.length === 0) return;

    const loc = {
      lat: position.coords.latitude,
      lng: position.coords.longitude,
    };

    // Skip if not moved significantly (>15m)
    if (lastLocationRef.current) {
      const dlat = loc.lat - lastLocationRef.current.lat;
      const dlng = loc.lng - lastLocationRef.current.lng;
      const approxMeters = Math.sqrt(dlat * dlat + dlng * dlng) * 111_000;
      if (approxMeters < 15) return;
    }

    lastLocationRef.current = loc;

    // Update all active deals in parallel
    const promises = activeDealIdsRef.current.map((dealId) =>
      supabase.functions.invoke('update-arrival-status', {
        body: { meeting_id: dealId, location: loc },
      }).catch((err) => {
        console.error('[BackgroundDealTracker] Update error for deal', dealId, err);
      })
    );

    await Promise.allSettled(promises);
  }, [user]);

  // Fetch active deals for the current user
  const fetchActiveDeals = useCallback(async () => {
    if (!user) {
      activeDealIdsRef.current = [];
      return;
    }

    try {
      const { data, error } = await supabase
        .from('deals')
        .select('id, meetup_time, qr_valid_window_start, qr_valid_window_end')
        .or(`buyer_id.eq.${user.id},seller_id.eq.${user.id}`)
        .in('status', ['pending', 'active'])
        .is('deleted_at', null);

      if (error) {
        console.error('[BackgroundDealTracker] Fetch error:', error);
        activeDealIdsRef.current = [];
        return;
      }

      if (!data || data.length === 0) {
        activeDealIdsRef.current = [];
        return;
      }

      // Only track deals within a reasonable time window:
      // from 30 minutes before meetup to 1 hour after
      const now = new Date();
      const relevantDeals = data.filter((deal) => {
        const meetupTime = new Date(deal.meetup_time);
        const trackStart = new Date(meetupTime.getTime() - 30 * 60 * 1000);
        const trackEnd = new Date(meetupTime.getTime() + 60 * 60 * 1000);
        return now >= trackStart && now <= trackEnd;
      });

      activeDealIdsRef.current = relevantDeals.map((d) => d.id);
    } catch (err) {
      console.error('[BackgroundDealTracker] Error:', err);
      activeDealIdsRef.current = [];
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      // Clean up when logged out
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      activeDealIdsRef.current = [];
      return;
    }

    // Check for active deals immediately and then every 2 minutes
    fetchActiveDeals();
    intervalRef.current = setInterval(fetchActiveDeals, 2 * 60 * 1000);

    // Start watching position
    if (navigator.geolocation && watchIdRef.current === null) {
      watchIdRef.current = navigator.geolocation.watchPosition(
        (pos) => {
          if (activeDealIdsRef.current.length > 0) {
            sendLocationUpdate(pos);
          }
        },
        (err) => {
          console.warn('[BackgroundDealTracker] GPS error:', err.message);
        },
        {
          enableHighAccuracy: true,
          maximumAge: 10_000,
          timeout: 15_000,
        }
      );
    }

    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [user, fetchActiveDeals, sendLocationUpdate]);
}
